import { validateSemanticFactBinding } from './semanticFactBinding';
/**
 * Operator attestations — the resolution path for findings that name no fact.
 *
 * `internalNumber` and `grounding` blocks are settled by CORRECTING A VALUE:
 * they name a governed fact, the answer writes to its SED field, and the
 * detector can no longer fire on it. `absenceAssertionAudit` and
 * `caveatHonesty` are not like that. "Redundancy has not been confirmed" is not
 * settled by typing a number, and neither is "this claim declares more
 * confidence than its citations support" — they are settled by a human who
 * knows something the evidence does not, recording that they stand behind the
 * statement.
 *
 * Before this existed those two detectors were DEAD ENDS: the dialog could
 * raise the question but had nowhere to put the answer, so the only recourse
 * was rerunning against unchanged evidence, which reproduces the finding.
 *
 * Persisted as the platform's existing `SiteAuditOverride` (`type: 'validate'`)
 * rather than a new store: it is already company-scoped, already carries
 * `createdBy` / `createdAt` for audit, and already requires a rationale.
 */
import type { CreateSiteAuditOverride, SiteAuditOverride } from '../../types/site';
import type { SEDAmendment } from '../../types/sedAmendments';
import { factIdToSedPath } from '../grounding/buildCanonicalFactStore';
import { isComplementaryFloodAcreageSplit, isTruthViolation, truthCheckId, type TruthQuestion } from './qaSeverityPolicy';
import { isQualifiedColocationFallbackClaim } from '../grounding/quantitySourceRoles';

/**
 * Strip internal HTML comments (e.g., `<!-- SOURCE-CONFLICT: ... -->`) from
 * reader-facing truth-question claim excerpts.
 */
export function stripHtmlComments(text: string): string {
  return text
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/^(?:[^<\n]{0,40}?-->\s*)+/g, '')
    .replace(/<!--[\s\S]*$/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

/**
 * An attestation is only honoured with a RATIONALE. "I stand behind this" with
 * no reason recorded is indistinguishable from clicking through a dialog, and
 * this override suppresses a truth-tier finding — the one place in the system
 * where a human overrules the evidence. Mirrors the discipline
 * `citationDensityGate` already applies via `validateOverrideRationale`.
 */
const MIN_RATIONALE_CHARS = 12;

export type RetainedUnknownAuditOverride = SiteAuditOverride & {
  type: 'retain-unknown' | 'validate';
  checkId: string;
  resolution: 'retained-unknown';
  rationale: string;
  assumptionLabel?: string;
  sourceType?: string;
  sourceCitation?: string;
  evidenceDocumentId?: string;
  evidenceAttachment?: string;
};

export function isRetainedUnknownOverride(
  override: SiteAuditOverride,
): override is RetainedUnknownAuditOverride {
  if (!('type' in override) || (override.type !== 'retain-unknown' && override.type !== 'validate')) {
    return false;
  }
  const row = override as { checkId?: unknown; resolution?: unknown; rationale?: unknown };
  if (typeof row.checkId !== 'string' || !row.checkId.startsWith('truth-')) return false;
  if (override.type !== 'retain-unknown' && row.resolution !== 'retained-unknown') return false;
  return typeof row.rationale === 'string' && row.rationale.trim().length >= MIN_RATIONALE_CHARS;
}

export function isPendingCorrectionOverride(
  override: SiteAuditOverride,
): boolean {
  if (!('type' in override) || override.type !== 'validate') return false;
  const row = override as { checkId?: unknown; resolution?: unknown };
  if (typeof row.checkId !== 'string' || !row.checkId.startsWith('truth-')) return false;
  const res = typeof row.resolution === 'string' ? row.resolution.trim() : '';
  return (
    res === 'pending-correction' ||
    res === 'corrected-pending' ||
    res === 'feasibility-assumption-pending'
  );
}

export interface TruthQuestionClassification {
  category: 'evidence-correction' | 'narrative-repair' | 'explicit-unknown' | 'assumption-or-attestation';
  badgeLabel: string;
  actionGuidance: string;
}

export function classifyTruthQuestion(
  question: TruthQuestion,
  hasWritableSedPath: boolean,
): TruthQuestionClassification {
  if (question.kind === 'value' && hasWritableSedPath) {
    return {
      category: 'evidence-correction',
      badgeLabel: 'Evidence correction / upload',
      actionGuidance:
        'Select or enter the source-supported value to amend the SED, or retain unknown if the fact is not yet established.',
    };
  }
  if (question.kind !== 'attestation' && !hasWritableSedPath) {
    return {
      category: 'narrative-repair',
      badgeLabel: 'Unbound / contextual figure — narrative repair or explicit unknown',
      actionGuidance:
        'Contextual or derived figure (does not overwrite a stored SED slot). Apply a source-grounded narrative correction, record an explicit unknown with rationale, or confirm with supporting evidence.',
    };
  }
  if (
    question.detector === 'waterUseScreening' ||
    question.detector === 'sourceRoleTruth' ||
    question.detector === 'internalNumberFindings'
  ) {
    return {
      category: 'narrative-repair',
      badgeLabel: 'Source-grounded narrative repair or explicit unknown',
      actionGuidance:
        'Provide a source-grounded narrative replacement, record an explicit unknown with reason, or attach supporting primary evidence.',
    };
  }
  if (question.detector === 'absenceAssertionAudit') {
    return {
      category: 'explicit-unknown',
      badgeLabel: 'Explicit unknown or primary-source confirmation',
      actionGuidance:
        'Retain as an explicit unknown if unverified, or provide a source-grounded narrative correction / primary document citation.',
    };
  }
  return {
    category: 'assumption-or-attestation',
    badgeLabel: 'Labelled assumption, narrative repair, or evidence confirmation',
    actionGuidance:
      'Label as a feasibility-stage planning assumption, provide a source-grounded narrative correction, or confirm with supporting evidence.',
  };
}

export function isTruthAttestation(
  override: SiteAuditOverride,
): override is SiteAuditOverride & { type: 'validate'; checkId: string; rationale?: string } {
  if (!('type' in override) || override.type !== 'validate') return false;
  if (isRetainedUnknownOverride(override)) return false;
  if (isPendingCorrectionOverride(override)) return false;
  const row = override as { checkId?: unknown; rationale?: unknown; resolution?: unknown };
  const res = typeof row.resolution === 'string' ? row.resolution.trim() : '';
  if (res === 'dismissed' || res === 'acknowledged' || res === 'retained-unknown') return false;
  if (typeof row.checkId !== 'string' || !row.checkId.startsWith('truth-')) return false;
  return typeof row.rationale === 'string' && row.rationale.trim().length >= MIN_RATIONALE_CHARS;
}

export type AuditPersistenceConflictCode =
  | 'AUDIT_OVERRIDE_CONFLICT'
  | 'SED_VERSION_CONFLICT'
  | 'AUDIT_OPERATION_ID_MISMATCH'
  | 'SED_OPERATION_ID_MISMATCH';

export class AuditPersistenceConflictError extends Error {
  readonly code: AuditPersistenceConflictCode;
  readonly status = 409 as const;
  readonly currentVersion?: number;
  readonly currentFingerprint?: string;

  constructor(
    code: AuditPersistenceConflictCode,
    message: string,
    currentVersion?: number,
    currentFingerprint?: string,
  ) {
    super(message);
    this.name = 'AuditPersistenceConflictError';
    this.code = code;
    this.currentVersion = currentVersion;
    this.currentFingerprint = currentFingerprint;
  }
}

let _auditOperationSeq = 0;

/**
 * Generates a session-safe unique operation ID so distinct mounts/sessions
 * never collide, while callers can retain the returned ID in memory to
 * idempotently retry the same payload.
 */
export function generateAuditOperationId(prefix = 'audit-op'): string {
  _auditOperationSeq += 1;
  const uuid =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${_auditOperationSeq}`;
  return `${prefix}:${uuid}`;
}

export function computeAuditOverridePayloadSignature(
  override: Partial<SiteAuditOverride> & Record<string, unknown>,
): string {
  const typeVal =
    typeof override.type === 'string'
      ? override.type
      : typeof override.gate === 'string'
        ? `gate:${override.gate}`
        : 'legacy';
  const fields = Array.isArray(override.fields)
    ? [...override.fields].map(String).sort()
    : undefined;
  return JSON.stringify({
    type: typeVal,
    gate: typeof override.gate === 'string' ? override.gate.trim() : '',
    checkId: typeof override.checkId === 'string' ? override.checkId.trim() : '',
    factKey: typeof override.factKey === 'string' ? override.factKey.trim() : '',
    stableKey: typeof override.stableKey === 'string' ? override.stableKey.trim() : '',
    canonicalKey: typeof override.canonicalKey === 'string' ? override.canonicalKey.trim() : '',
    canonicalValue:
      override.canonicalValue !== undefined && override.canonicalValue !== null
        ? String(override.canonicalValue).trim()
        : '',
    findingId: typeof override.findingId === 'string' ? override.findingId.trim() : '',
    until: typeof override.until === 'string' ? override.until.trim() : '',
    retractsOverrideId:
      typeof override.retractsOverrideId === 'string'
        ? override.retractsOverrideId.trim()
        : '',
    threshold: typeof override.threshold === 'number' ? override.threshold : null,
    fields: fields ?? null,
    resolution: typeof override.resolution === 'string' ? override.resolution.trim() : '',
    rationale: typeof override.rationale === 'string' ? override.rationale.trim() : '',
    assumptionLabel: typeof override.assumptionLabel === 'string' ? override.assumptionLabel.trim() : '',
    sourceType: typeof override.sourceType === 'string' ? override.sourceType.trim() : '',
    sourceCitation: typeof override.sourceCitation === 'string' ? override.sourceCitation.trim() : '',
    evidenceDocumentId:
      typeof override.evidenceDocumentId === 'string' ? override.evidenceDocumentId.trim() : '',
    evidenceAttachment:
      typeof override.evidenceAttachment === 'string' ? override.evidenceAttachment.trim() : '',
    replacementText: typeof override.replacementText === 'string' ? override.replacementText.trim() : '',
    priorEvidence: typeof override.priorEvidence === 'string' ? override.priorEvidence.trim() : '',
    reportTypeId: typeof override.reportTypeId === 'string' ? override.reportTypeId.trim() : '',
  });
}

export function matchesAuditOverrideOperationPayload(
  existing: SiteAuditOverride,
  incoming: Partial<SiteAuditOverride> & Record<string, unknown>,
): boolean {
  return (
    computeAuditOverridePayloadSignature(existing as unknown as Record<string, unknown>) ===
    computeAuditOverridePayloadSignature(incoming)
  );
}

export type SedAmendmentSignatureInput = Partial<
  Pick<
    SEDAmendment,
    'sedId' | 'variablePath' | 'domain' | 'oldValue' | 'newValue' | 'unit' | 'reason' | 'source'
  >
>;

export function computeSedAmendmentPayloadSignature(
  amendment: SedAmendmentSignatureInput,
): string {
  const path = (amendment.variablePath ?? '').trim();
  const derivedDomain = path ? (path.split('.')[0] ?? '') : '';
  const domain = (amendment.domain ?? derivedDomain).trim();
  const src = amendment.source;
  return JSON.stringify({
    sedId: (amendment.sedId ?? '').trim(),
    variablePath: path,
    domain,
    oldValue: amendment.oldValue !== undefined ? amendment.oldValue : null,
    newValue: amendment.newValue ?? null,
    unit: typeof amendment.unit === 'string' ? amendment.unit.trim() : '',
    reason: (amendment.reason ?? '').trim(),
    source: src
      ? {
          type: typeof src.type === 'string' ? src.type.trim() : '',
          label: typeof src.label === 'string' ? src.label.trim() : '',
          url: typeof src.url === 'string' ? src.url.trim() : '',
          documentId: typeof src.documentId === 'string' ? src.documentId.trim() : '',
          excerpt: typeof (src as { excerpt?: string }).excerpt === 'string' ? (src as { excerpt?: string }).excerpt!.trim() : '',
        }
      : null,
  });
}

export function matchesSedAmendmentOperationPayload(
  existing: SedAmendmentSignatureInput,
  incoming: SedAmendmentSignatureInput,
): boolean {
  return (
    computeSedAmendmentPayloadSignature(existing) ===
    computeSedAmendmentPayloadSignature(incoming)
  );
}

export class AuditPersistenceForbiddenError extends Error {
  readonly code = 'FORBIDDEN_SITE_WRITE' as const;
  readonly status = 403 as const;

  constructor(message: string) {
    super(message);
    this.name = 'AuditPersistenceForbiddenError';
  }
}

export function stripOverrideVersionSuffix(id: string): string {
  return id.replace(/#v\d+$/, '');
}

export function deriveAuditOverrideLogicalKey(
  override: Partial<SiteAuditOverride> & { id?: string },
): string {
  if (typeof override.logicalKey === 'string' && override.logicalKey.trim().length > 0) {
    return override.logicalKey.trim();
  }
  const rawId = typeof override.id === 'string' ? stripOverrideVersionSuffix(override.id.trim()) : '';
  const scope = typeof override.reportTypeId === 'string' && override.reportTypeId.trim() ? override.reportTypeId.trim() : '*';
  if ('type' in override && override.type) {
    if (override.type === 'retract') {
      const retractTarget = stripOverrideVersionSuffix((override.retractsOverrideId ?? '').trim());
      return retractTarget || rawId || 'retract';
    }
    if (override.type === 'validate' || override.type === 'retain-unknown') {
      if (rawId.startsWith('validate:') || rawId.startsWith('retain-unknown:')) {
        return rawId;
      }
      const checkOrFact = (override.checkId ?? override.factKey ?? '').trim();
      if (checkOrFact) return `check:${scope}:${checkOrFact}`;
    }
    if (override.type === 'reconcile') {
      if (rawId.startsWith('recon:')) return rawId;
      const key = (override.canonicalKey ?? '').trim();
      if (key) return `reconcile:${scope}:${key}`;
    }
    if (override.type === 'dismiss') {
      if (rawId.startsWith('dismiss:')) return rawId;
      const fid = (override.findingId ?? '').trim();
      if (fid) return `dismiss:${scope}:${fid}`;
    }
    if (override.type === 'density') {
      return rawId || `density:${scope}`;
    }
    if (override.type === 'coverage') {
      const fields = Array.isArray(override.fields) ? [...override.fields].sort().join(',') : '';
      return rawId || `coverage:${scope}:${fields}`;
    }
  }
  if ('gate' in override && override.gate) {
    const fields = Array.isArray(override.fields) ? [...override.fields].sort().join(',') : '';
    return rawId || `gate:${scope}:${override.gate}:${fields}`;
  }
  return rawId || 'override';
}

/**
 * Replays the append-only `site.auditOverrides` log in order, applying
 * `supersedesOverrideId`, logical-key replacement, and `{ type: 'retract' }`
 * entries so readers see only the currently active decisions while the stored
 * array preserves full immutable history.
 */
export function resolveActiveAuditOverrides(
  overrides: readonly SiteAuditOverride[] | undefined,
): SiteAuditOverride[] {
  if (!overrides || overrides.length === 0) return [];
  const activeById = new Map<string, SiteAuditOverride>();
  const activeIdByLogicalKey = new Map<string, string>();

  const removeActiveByTarget = (targetIdOrKey: string) => {
    const cleanTarget = targetIdOrKey.trim();
    if (!cleanTarget) return;
    const baseTarget = stripOverrideVersionSuffix(cleanTarget);
    if (activeById.has(cleanTarget)) {
      const existing = activeById.get(cleanTarget)!;
      activeById.delete(cleanTarget);
      const lk = deriveAuditOverrideLogicalKey(existing);
      if (activeIdByLogicalKey.get(lk) === cleanTarget) {
        activeIdByLogicalKey.delete(lk);
      }
    }
    const mappedId = activeIdByLogicalKey.get(cleanTarget) ?? activeIdByLogicalKey.get(baseTarget);
    if (mappedId && activeById.has(mappedId)) {
      activeById.delete(mappedId);
      activeIdByLogicalKey.delete(cleanTarget);
      activeIdByLogicalKey.delete(baseTarget);
    }
    for (const [id, entry] of [...activeById.entries()]) {
      if (
        stripOverrideVersionSuffix(id) === baseTarget ||
        deriveAuditOverrideLogicalKey(entry) === cleanTarget ||
        deriveAuditOverrideLogicalKey(entry) === baseTarget
      ) {
        activeById.delete(id);
        const lk = deriveAuditOverrideLogicalKey(entry);
        if (activeIdByLogicalKey.get(lk) === id) {
          activeIdByLogicalKey.delete(lk);
        }
      }
    }
  };

  for (const override of overrides) {
    if (!override || typeof override !== 'object') continue;
    if (typeof override.supersedesOverrideId === 'string' && override.supersedesOverrideId.trim()) {
      removeActiveByTarget(override.supersedesOverrideId);
    }
    if ('type' in override && override.type === 'retract') {
      if (typeof override.retractsOverrideId === 'string') {
        removeActiveByTarget(override.retractsOverrideId);
      }
      if (typeof override.logicalKey === 'string' && override.logicalKey.trim()) {
        removeActiveByTarget(override.logicalKey);
      }
      continue;
    }
    const logicalKey = deriveAuditOverrideLogicalKey(override);
    const priorId = activeIdByLogicalKey.get(logicalKey);
    if (priorId && priorId !== override.id) {
      activeById.delete(priorId);
    }
    activeById.set(override.id, override);
    activeIdByLogicalKey.set(logicalKey, override.id);
  }

  return [...activeById.values()];
}

export function computeAuditOverridesVersion(
  site: { auditOverrides?: readonly SiteAuditOverride[]; auditOverridesVersion?: number } | null | undefined,
): number {
  if (!site) return 0;
  const listLen = Array.isArray(site.auditOverrides) ? site.auditOverrides.length : 0;
  if (typeof site.auditOverridesVersion === 'number' && Number.isFinite(site.auditOverridesVersion)) {
    return Math.max(site.auditOverridesVersion, listLen);
  }
  return listLen;
}

export function computeAuditOverridesFingerprint(
  overrides: readonly SiteAuditOverride[] | undefined,
): string {
  const list = overrides ?? [];
  return `v${list.length}:${list
    .map((o, idx) => `${o.sequence ?? idx + 1}:${o.id}:${'type' in o && o.type ? o.type : ('gate' in o && o.gate ? o.gate : 'legacy')}:${o.operationId ?? ''}`)
    .join('|')}`;
}

/**
 * Ensures any direct `auditOverrides` array write preserves all prior history
 * entries verbatim, converting in-place replacements into appended superseding
 * entries and in-place removals into appended `{ type: 'retract' }` entries.
 */
function canonicalizeHistoricalRecord(value: unknown): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(canonicalizeHistoricalRecord);
  const rec = value as Record<string, unknown>;
  const sortedKeys = Object.keys(rec)
    .filter((k) => rec[k] !== undefined)
    .sort();
  const out: Record<string, unknown> = {};
  for (const k of sortedKeys) {
    out[k] = canonicalizeHistoricalRecord(rec[k]);
  }
  return out;
}

export function isVerbatimAuditOverrideRecordEqual(
  prior: SiteAuditOverride,
  candidate: SiteAuditOverride | undefined,
): boolean {
  if (!candidate) return false;
  return (
    JSON.stringify(canonicalizeHistoricalRecord(prior)) ===
    JSON.stringify(canonicalizeHistoricalRecord(candidate))
  );
}

export function preserveAppendOnlyAuditOverrides(
  priorOverrides: readonly SiteAuditOverride[] | undefined,
  incomingOverrides: readonly SiteAuditOverride[] | undefined,
  nowOrActor:
    | string
    | { uid?: string; email?: string; nowIso?: string } = new Date().toISOString(),
): SiteAuditOverride[] {
  const nowIso =
    typeof nowOrActor === 'string'
      ? nowOrActor
      : nowOrActor?.nowIso ?? new Date().toISOString();
  const fallbackActor =
    typeof nowOrActor === 'object' && nowOrActor && typeof nowOrActor.uid === 'string'
      ? { uid: nowOrActor.uid, ...(nowOrActor.email ? { email: nowOrActor.email } : {}) }
      : undefined;

  if (!priorOverrides || priorOverrides.length === 0) {
    return [...(incomingOverrides ?? [])];
  }
  const incoming = incomingOverrides ?? [];
  if (
    incoming.length === priorOverrides.length &&
    priorOverrides.every((p, idx) => isVerbatimAuditOverrideRecordEqual(p, incoming[idx]))
  ) {
    return [...priorOverrides];
  }

  // Always retain priorOverrides verbatim at indices 0..priorOverrides.length-1
  const result: SiteAuditOverride[] = [...priorOverrides];
  const priorById = new Map<string, SiteAuditOverride>();
  for (const p of priorOverrides) {
    priorById.set(p.id, p);
  }

  const incomingIds = new Set<string>();
  const incomingLogicalKeys = new Set<string>();

  for (const inc of incoming) {
    incomingIds.add(inc.id);
    incomingLogicalKeys.add(deriveAuditOverrideLogicalKey(inc));
    const existing = priorById.get(inc.id);
    if (!existing) {
      const nextSeq = result.length + 1;
      const active = resolveActiveAuditOverrides(result);
      const lk = deriveAuditOverrideLogicalKey(inc);
      const priorActive = active.find((a) => deriveAuditOverrideLogicalKey(a) === lk);
      result.push({
        ...inc,
        logicalKey: inc.logicalKey ?? lk,
        sequence: inc.sequence ?? nextSeq,
        ...(priorActive && !inc.supersedesOverrideId ? { supersedesOverrideId: priorActive.id } : {}),
        ...(fallbackActor && !inc.createdBy ? { createdBy: fallbackActor } : {}),
      });
    } else if (!isVerbatimAuditOverrideRecordEqual(existing, inc)) {
      const nextSeq = result.length + 1;
      const baseId = stripOverrideVersionSuffix(inc.id);
      const lk = deriveAuditOverrideLogicalKey(inc);
      const active = resolveActiveAuditOverrides(result);
      const priorActive = active.find(
        (a) => deriveAuditOverrideLogicalKey(a) === lk || stripOverrideVersionSuffix(a.id) === baseId,
      ) ?? existing;
      result.push({
        ...inc,
        id: `${baseId}#v${nextSeq}`,
        logicalKey: lk,
        supersedesOverrideId: priorActive.id,
        sequence: nextSeq,
        createdAt: inc.createdAt || nowIso,
        ...(fallbackActor && !inc.createdBy ? { createdBy: fallbackActor } : {}),
      });
    }
  }

  const activeBeforeRemoval = resolveActiveAuditOverrides(priorOverrides);
  for (const activeEntry of activeBeforeRemoval) {
    const lk = deriveAuditOverrideLogicalKey(activeEntry);
    const baseId = stripOverrideVersionSuffix(activeEntry.id);
    const stillPresent =
      incomingIds.has(activeEntry.id) ||
      incomingLogicalKeys.has(lk) ||
      [...incomingIds].some((id) => stripOverrideVersionSuffix(id) === baseId);
    if (!stillPresent) {
      const nextSeq = result.length + 1;
      result.push({
        id: `retract:${baseId}#v${nextSeq}`,
        type: 'retract',
        retractsOverrideId: activeEntry.id,
        logicalKey: lk,
        supersedesOverrideId: activeEntry.id,
        sequence: nextSeq,
        ...('checkId' in activeEntry && activeEntry.checkId ? { checkId: activeEntry.checkId } : {}),
        ...('canonicalKey' in activeEntry && activeEntry.canonicalKey ? { canonicalKey: activeEntry.canonicalKey } : {}),
        ...('findingId' in activeEntry && activeEntry.findingId ? { findingId: activeEntry.findingId } : {}),
        ...(activeEntry.reportTypeId ? { reportTypeId: activeEntry.reportTypeId } : {}),
        ...(fallbackActor ? { createdBy: fallbackActor } : {}),
        resolution: 'retracted',
        rationale: `Retracted override ${activeEntry.id}`,
        createdAt: nowIso,
      });
    }
  }

  return result;
}

export function attestedCheckIds(
  overrides: readonly SiteAuditOverride[] | undefined,
  reportTypeId?: string,
): ReadonlySet<string> {
  const out = new Set<string>();
  for (const override of resolveActiveAuditOverrides(overrides)) {
    if (override.reportTypeId && reportTypeId && override.reportTypeId !== reportTypeId) continue;
    if (isRetainedUnknownOverride(override) || isPendingCorrectionOverride(override)) {
      out.delete((override as { checkId: string }).checkId);
      continue;
    }
    if (!isTruthAttestation(override)) continue;
    out.add(override.checkId);
  }
  return out;
}

export function retainedUnknownByCheckId(
  overrides: readonly SiteAuditOverride[] | undefined,
  reportTypeId?: string,
): ReadonlyMap<string, RetainedUnknownAuditOverride> {
  const out = new Map<string, RetainedUnknownAuditOverride>();
  for (const override of resolveActiveAuditOverrides(overrides)) {
    if (override.reportTypeId && reportTypeId && override.reportTypeId !== reportTypeId) continue;
    if (isRetainedUnknownOverride(override)) {
      out.set(override.checkId, override);
      continue;
    }
    if (isTruthAttestation(override)) {
      out.delete(override.checkId);
    }
  }
  return out;
}

export type SettlingAmendmentCandidate = Partial<
  Omit<
    Pick<
      SEDAmendment,
      | 'id'
      | 'sedId'
      | 'sedVersion'
      | 'status'
      | 'variablePath'
      | 'variableLabel'
      | 'createdAt'
      | 'appliedAt'
      | 'reason'
      | 'supersedesAmendmentId'
      | 'revertedFromAmendmentId'
    >,
    'source'
  > & {
    source?: SEDAmendment['source'] | string;
    field?: string;
    originalValue?: unknown;
    correctedValue?: unknown;
    timestamp?: string;
  }
>;

export function isRevertAmendmentRecord(
  a: SettlingAmendmentCandidate | null | undefined,
): boolean {
  if (!a || typeof a !== 'object') return false;
  if (a.status === 'reverted') return true;
  if (typeof a.revertedFromAmendmentId === 'string' && a.revertedFromAmendmentId.trim().length > 0) {
    return true;
  }
  if (typeof a.id === 'string' && a.id.trim().startsWith('revert-')) {
    return true;
  }
  if (typeof a.source === 'string' && a.source.trim().toLowerCase() === 'revert') {
    return true;
  }
  if (
    typeof a.source === 'object' &&
    a.source !== null &&
    typeof a.source.label === 'string' &&
    a.source.label.trim().toLowerCase() === 'revert'
  ) {
    return true;
  }
  if (
    typeof a.reason === 'string' &&
    /^revert(?:\s+via\s+ui\b|\s*[—:-]|\b.*restore\s+original\b)/i.test(a.reason.trim())
  ) {
    return true;
  }
  return false;
}

function amendmentStatusPrecedence(status: string | undefined): number {
  switch (status) {
    case 'reverted':
      return 6;
    case 'superseded':
      return 5;
    case 'rejected':
      return 4;
    case 'applied':
      return 3;
    case 'approved':
    case 'accepted':
      return 2;
    case 'pending':
      return 1;
    default:
      return 0;
  }
}

function parseAmendmentTimestampMs(a: SettlingAmendmentCandidate): number | null {
  const raw = a.appliedAt ?? a.createdAt ?? a.timestamp;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function isAmendmentStrictlyNewerThanRevert(
  applied: SettlingAmendmentCandidate,
  revertRec: SettlingAmendmentCandidate,
): boolean {
  if (
    typeof applied.id === 'string' &&
    applied.id &&
    (revertRec.revertedFromAmendmentId === applied.id ||
      revertRec.supersedesAmendmentId === applied.id)
  ) {
    return false;
  }
  if (
    typeof revertRec.id === 'string' &&
    revertRec.id &&
    applied.supersedesAmendmentId === revertRec.id
  ) {
    return true;
  }
  if (
    typeof applied.sedVersion === 'number' &&
    typeof revertRec.sedVersion === 'number' &&
    applied.sedVersion !== revertRec.sedVersion
  ) {
    return applied.sedVersion > revertRec.sedVersion;
  }
  const appliedMs = parseAmendmentTimestampMs(applied);
  const revertMs = parseAmendmentTimestampMs(revertRec);
  if (appliedMs !== null && revertMs !== null && appliedMs !== revertMs) {
    return appliedMs > revertMs;
  }
  return false;
}

export function resolveCurrentEffectiveAmendments(
  amendments: readonly SettlingAmendmentCandidate[] | undefined,
  currentSedId?: string,
  currentSedVersion?: number,
): {
  amendedPaths: Set<string>;
  amendedLabels: Set<string>;
  amendedIds: string[];
  effectiveStructured: SettlingAmendmentCandidate[];
} {
  const amendedPaths = new Set<string>();
  const amendedLabels = new Set<string>();
  const amendedIds: string[] = [];
  const effectiveStructured: SettlingAmendmentCandidate[] = [];
  if (!amendments || amendments.length === 0) {
    return { amendedPaths, amendedLabels, amendedIds, effectiveStructured };
  }

  const normalizedCurrentSedId = typeof currentSedId === 'string' ? currentSedId.trim() : '';
  const hasCurrentSedVersion =
    typeof currentSedVersion === 'number' && Number.isFinite(currentSedVersion);

  const inBandCandidates: SettlingAmendmentCandidate[] = [];
  const structuredCandidates: SettlingAmendmentCandidate[] = [];

  for (const a of amendments) {
    if (!a || typeof a !== 'object') continue;
    const entrySedId = typeof a.sedId === 'string' ? a.sedId.trim() : '';
    if (normalizedCurrentSedId && (!entrySedId || entrySedId !== normalizedCurrentSedId)) {
      continue;
    }
    const isInBandSedAmendment =
      a.status === undefined &&
      typeof a.field === 'string' &&
      a.field.trim().length > 0 &&
      'correctedValue' in a;
    if (isInBandSedAmendment) {
      if (
        hasCurrentSedVersion &&
        (typeof a.sedVersion !== 'number' || a.sedVersion !== currentSedVersion)
      ) {
        continue;
      }
      inBandCandidates.push(a);
      continue;
    }
    if (
      hasCurrentSedVersion &&
      typeof a.sedVersion === 'number' &&
      a.sedVersion > currentSedVersion!
    ) {
      continue;
    }
    structuredCandidates.push(a);
  }

  const supersededOrRevertedIds = new Set<string>();
  for (const c of [...structuredCandidates, ...inBandCandidates]) {
    if (c.status === 'rejected' || c.status === 'pending') {
      continue;
    }
    if (typeof c.supersedesAmendmentId === 'string' && c.supersedesAmendmentId.trim()) {
      supersededOrRevertedIds.add(c.supersedesAmendmentId.trim());
    }
    if (typeof c.revertedFromAmendmentId === 'string' && c.revertedFromAmendmentId.trim()) {
      supersededOrRevertedIds.add(c.revertedFromAmendmentId.trim());
    }
  }

  const computeEffectiveStatus = (
    c: SettlingAmendmentCandidate,
  ): SEDAmendment['status'] => {
    if (c.status !== 'pending' && c.status !== 'rejected' && isRevertAmendmentRecord(c)) {
      return 'reverted';
    }
    const cid = typeof c.id === 'string' ? c.id.trim() : '';
    if (cid && supersededOrRevertedIds.has(cid) && (c.status === 'applied' || !c.status)) {
      return 'superseded';
    }
    return c.status ?? 'pending';
  };

  const mergedById = new Map<string, SettlingAmendmentCandidate>();
  const anonymousStructured: SettlingAmendmentCandidate[] = [];

  for (const raw of structuredCandidates) {
    const effStatus = computeEffectiveStatus(raw);
    const normalized: SettlingAmendmentCandidate = { ...raw, status: effStatus };
    const cid = typeof raw.id === 'string' ? raw.id.trim() : '';
    if (!cid) {
      anonymousStructured.push(normalized);
      continue;
    }
    const prev = mergedById.get(cid);
    if (!prev) {
      mergedById.set(cid, normalized);
      continue;
    }
    const winningStatus =
      amendmentStatusPrecedence(normalized.status) >= amendmentStatusPrecedence(prev.status)
        ? normalized.status
        : prev.status;
    mergedById.set(cid, {
      ...prev,
      ...normalized,
      status: winningStatus,
    });
  }

  const allMergedStructured = [...mergedById.values(), ...anonymousStructured];
  const structuredMentionedPaths = new Set<string>();
  const revertsByVarKey = new Map<string, SettlingAmendmentCandidate[]>();

  const varKeyOf = (c: SettlingAmendmentCandidate): string =>
    (typeof c.variablePath === 'string' && c.variablePath.trim()) ||
    (typeof c.variableLabel === 'string' && c.variableLabel.trim()) ||
    '';

  for (const c of allMergedStructured) {
    if (typeof c.variablePath === 'string' && c.variablePath.trim()) {
      structuredMentionedPaths.add(c.variablePath.trim());
    }
    if (
      c.status === 'reverted' ||
      (c.status !== 'pending' && c.status !== 'rejected' && isRevertAmendmentRecord(c))
    ) {
      const vk = varKeyOf(c);
      if (vk) {
        const list = revertsByVarKey.get(vk) ?? [];
        list.push(c);
        revertsByVarKey.set(vk, list);
      }
    }
  }

  for (const c of allMergedStructured) {
    if (c.status !== 'applied') continue;
    const vk = varKeyOf(c);
    const reverts = vk ? revertsByVarKey.get(vk) : undefined;
    if (reverts && reverts.some((rev) => !isAmendmentStrictlyNewerThanRevert(c, rev))) {
      continue;
    }
    effectiveStructured.push(c);
    if (c.variablePath) amendedPaths.add(c.variablePath);
    if (c.variableLabel) amendedLabels.add(c.variableLabel);
    if (c.id) amendedIds.push(c.id);
  }

  const latestInBandByField = new Map<string, SettlingAmendmentCandidate>();
  for (const inBand of inBandCandidates) {
    const field = inBand.field!.trim();
    latestInBandByField.set(field, inBand);
  }
  for (const [field, lastInBand] of latestInBandByField.entries()) {
    if (structuredMentionedPaths.has(field)) {
      continue;
    }
    if (isRevertAmendmentRecord(lastInBand)) {
      continue;
    }
    amendedPaths.add(field);
  }

  return { amendedPaths, amendedLabels, amendedIds, effectiveStructured };
}

export function filterUnsettledTruthQuestions(
  questions: readonly TruthQuestion[] | undefined,
  overrides?: readonly SiteAuditOverride[],
  reportTypeId?: string,
  amendments?: readonly SettlingAmendmentCandidate[],
  currentSedId?: string,
  currentSedVersion?: number,
  qaFindings?: Record<string, unknown> | null,
): TruthQuestion[] {
  if (!questions || questions.length === 0) return [];
  const attested = attestedCheckIds(overrides, reportTypeId);
  const { amendedPaths, amendedLabels, amendedIds } = resolveCurrentEffectiveAmendments(
    amendments,
    currentSedId,
    currentSedVersion,
  );
  const formatCandidates = (
    candidates: readonly { value: string; label: string }[] | undefined,
  ): Array<{ value: string; label: string }> =>
    (candidates ?? []).map((c) => {
      const val = (c.value ?? '').trim();
      const lbl = (c.label ?? '').trim();
      if (!val || val === 'confirmed' || val === 'retract' || lbl.includes(val)) {
        return { value: c.value, label: c.label };
      }
      return { value: c.value, label: `${lbl}: ${val}` };
    });

  const sanitizeQuestion = (q: TruthQuestion): TruthQuestion => {
    const formattedCandidates = formatCandidates(q.candidates);
    if (q.kind !== 'value' || !q.factId) {
      return formattedCandidates.some((c, i) => c.label !== q.candidates?.[i]?.label)
        ? { ...q, candidates: formattedCandidates }
        : q;
    }
    const observedCandidate = (q.candidates ?? []).find((c) =>
      /stated in the report|value in report/i.test(c.label),
    );
    const expectedCandidate = (q.candidates ?? []).find((c) =>
      /canonical evidence|^evidence\b/i.test(c.label),
    );
    const check = validateSemanticFactBinding({
      factId: q.factId,
      claimText: q.reportClaim,
      observedText: observedCandidate?.value,
      expectedText: expectedCandidate?.value,
      message: q.question,
      candidateValues: (q.candidates ?? []).map((c) => `${c.label} ${c.value}`),
    });
    if (check.ok) {
      return {
        ...q,
        candidates: formattedCandidates,
      };
    }
    return {
      ...q,
      factId: undefined,
      question: `Contextual figure cannot be mapped to "${q.factId}" (${check.reason})`,
      candidates: [],
      allowFreeText: false,
    };
  };
  const allClaimsContext = questions
    .map((q) => `${q.reportClaim ?? ''} ${(q.candidates ?? []).map((c) => c.value).join(' ')}`)
    .join(' ');
  const isSuppressedFalsePositive = (q: TruthQuestion): boolean => {
    const claimText = q.reportClaim ?? '';
    const questionText = q.question ?? '';
    if (
      (q.detector === 'sourceRoleTruth' ||
        q.factId === 'connectivity.sourceRole' ||
        q.factId === 'connectivity.data.sourceRole' ||
        /connectivity\.(?:data\.)?sourceRole/i.test(questionText)) &&
      isQualifiedColocationFallbackClaim(claimText)
    ) {
      return true;
    }
    if (
      q.detector === 'grounding' &&
      (q.factId === 'siteArea.acres' || /siteArea\.acres/i.test(questionText)) &&
      /\(\s*\d+(?:\.\d+)?\s*%\s*\)\s+in\s+zone\s+[ax]\b/i.test(claimText)
    ) {
      return true;
    }
    if (qaFindings && typeof qaFindings === 'object' && q.detector) {
      const bucket = qaFindings[q.detector];
      const rows: Record<string, unknown>[] = Array.isArray(bucket)
        ? (bucket as Record<string, unknown>[])
        : Array.isArray((bucket as { findings?: unknown[] } | undefined)?.findings)
          ? ((bucket as { findings: Record<string, unknown>[] }).findings)
          : Array.isArray((bucket as { violations?: unknown[] } | undefined)?.violations)
            ? ((bucket as { violations: Record<string, unknown>[] }).violations)
            : [];
      if (
        rows.length > 0 &&
        (q.detector === 'internalNumberFindings' ||
          q.detector === 'sourceRoleTruth' ||
          q.detector === 'waterUseScreening' ||
          q.detector === 'grounding')
      ) {
        const matching = rows.filter((r) => {
          const m = String(r.message ?? r.detail ?? r.kind ?? r.label ?? '').trim();
          const excerpt = String(r.excerpt ?? '').trim();
          return (
            (claimText.length > 0 && m === claimText) ||
            (excerpt.length > 0 && excerpt === claimText) ||
            (q.factId && r.canonicalKey === q.factId)
          );
        });
        if (matching.length > 0 && matching.every((r) => !isTruthViolation(q.detector, r))) {
          return true;
        }
      }
    }
    if (
      q.detector === 'internalNumberFindings' &&
      (q.factId === 'flood_area_acres' || /flood_area_acres/i.test(questionText))
    ) {
      const candidateNums = (q.candidates ?? [])
        .map((c) => Number(String(c.value ?? '').replace(/[^0-9.-]/g, '')))
        .filter(Number.isFinite);
      if (isComplementaryFloodAcreageSplit(candidateNums, `${claimText} ${allClaimsContext}`)) {
        return true;
      }
      if (
        /(s*affected-totals*)/i.test(claimText) &&
        candidateNums.length === 2 &&
        Math.min(...candidateNums) > 0 &&
        Math.max(...candidateNums) / Math.min(...candidateNums) >= 5
      ) {
        return true;
      }
    }
    if (q.detector === 'waterUseScreening') {
      const claim = claimText;
      const hasGenuineMislabeledSignal =
        /potentially\s+0\s+gal\/day|withdrawal\s+or\s+consumption\s+not\s+specified|\|\s*Suggested\s+Follow\s+Up:/i.test(
          claim,
        );
      if (
        !hasGenuineMislabeledSignal &&
        /\b(?:modeled|withdrawal\s+sensitivity|cooling\s+sensitivity)\b/i.test(claim) &&
        /\b(?:withdrawal|makeup|consumption|evaporation)\b/i.test(claim) &&
        (/\bphase\s*[12]\b/i.test(claim) ||
          /\b(?:wet-tower|wet\s+cooling-tower|adiabatic-assist|adiabatic\s+makeup)\b/i.test(claim))
      ) {
        return true;
      }
    }
    return false;
  };
  const sanitizedQuestions = questions
    .filter((q) => !isSuppressedFalsePositive(q))
    .map(sanitizeQuestion);
  const isSettled = (q: TruthQuestion): boolean => {
    if (q.id && attested.has(q.id)) return true;
    if (q.id && amendedIds.some((id) => id.startsWith(`amend-truth-${q.id}-`))) return true;
    if (q.factId) {
      if (amendedLabels.has(q.factId)) return true;
      const sedPath = factIdToSedPath(q.factId);
      if (sedPath && amendedPaths.has(sedPath)) return true;
    }
    return false;
  };
  return sanitizedQuestions.filter((q) => !isSettled(q));
}

/**
 * Build the override an attestation persists as.
 *
 * Returns the CREATE shape — `recordAuditOverride` stamps `id`, `createdAt`
 * and `createdBy` itself, so minting them here would either be discarded or,
 * worse, disagree with the audit trail.
 */
export function buildTruthAttestation(input: {
  detector: string;
  message: string;
  /**
   * Exact question/check identity emitted by `buildTruthQuestions`.
   *
   * The operator-facing `message` can be the quoted report claim, while the
   * question id is derived from the detector's raw finding message. Callers
   * that already hold the question must pass its id through unchanged.
   */
  checkId?: string;
  rationale: string;
  resolution?: string;
  assumptionLabel?: string;
  sourceType?: string;
  sourceCitation?: string;
  evidenceDocumentId?: string;
  evidenceAttachment?: string;
  replacementText?: string;
  priorEvidence?: string;
  reportTypeId?: string;
  runId?: string;
  operationId?: string;
  supersedesOverrideId?: string;
  logicalKey?: string;
}): CreateSiteAuditOverride {
  const checkId = input.checkId ?? truthCheckId(input.detector, input.message);
  const resolution = input.resolution ?? 'attested';
  const logicalKey = input.logicalKey ?? `check:${input.reportTypeId ?? '*'}:${checkId}`;
  if (resolution === 'retained-unknown') {
    return {
      type: 'retain-unknown',
      checkId,
      logicalKey,
      resolution: 'retained-unknown',
      rationale: input.rationale,
      ...(input.assumptionLabel ? { assumptionLabel: input.assumptionLabel } : {}),
      ...(input.sourceType ? { sourceType: input.sourceType } : {}),
      ...(input.sourceCitation ? { sourceCitation: input.sourceCitation } : {}),
      ...(input.evidenceDocumentId ? { evidenceDocumentId: input.evidenceDocumentId } : {}),
      ...(input.evidenceAttachment ? { evidenceAttachment: input.evidenceAttachment } : {}),
      ...(input.priorEvidence ? { priorEvidence: input.priorEvidence } : {}),
      ...(input.reportTypeId ? { reportTypeId: input.reportTypeId } : {}),
      ...(input.runId ? { runId: input.runId } : {}),
      ...(input.operationId ? { operationId: input.operationId } : {}),
      ...(input.supersedesOverrideId ? { supersedesOverrideId: input.supersedesOverrideId } : {}),
    };
  }
  return {
    type: 'validate',
    checkId,
    logicalKey,
    resolution,
    rationale: input.rationale,
    ...(input.assumptionLabel ? { assumptionLabel: input.assumptionLabel } : {}),
    ...(input.sourceType ? { sourceType: input.sourceType } : {}),
    ...(input.sourceCitation ? { sourceCitation: input.sourceCitation } : {}),
    ...(input.evidenceDocumentId ? { evidenceDocumentId: input.evidenceDocumentId } : {}),
    ...(input.evidenceAttachment ? { evidenceAttachment: input.evidenceAttachment } : {}),
    ...(input.replacementText ? { replacementText: input.replacementText } : {}),
    ...(input.priorEvidence ? { priorEvidence: input.priorEvidence } : {}),
    ...(input.reportTypeId ? { reportTypeId: input.reportTypeId } : {}),
    ...(input.runId ? { runId: input.runId } : {}),
    ...(input.operationId ? { operationId: input.operationId } : {}),
    ...(input.supersedesOverrideId ? { supersedesOverrideId: input.supersedesOverrideId } : {}),
  };
}

export class AuditOverrideValidationError extends Error {
  readonly code = 'INVALID_OVERRIDE_PAYLOAD' as const;
  constructor(message: string) {
    super(message);
    this.name = 'AuditOverrideValidationError';
  }
}

const ALLOWED_APPEND_OVERRIDE_TYPES = new Set([
  'reconcile',
  'validate',
  'density',
  'coverage',
  'retain-unknown',
  'dismiss',
]);

const ALLOWED_APPEND_OVERRIDE_GATES = new Set([
  'intent-sed-reconciliation',
  'provider-coverage',
  'citation-density',
  'provider-precedence',
]);

const ALLOWED_OVERRIDE_KEYS = new Set([
  'id',
  'type',
  'gate',
  'reportTypeId',
  'runId',
  'operationId',
  'logicalKey',
  'supersedesOverrideId',
  'retractsOverrideId',
  'sequence',
  'createdAt',
  'createdBy',
  'canonicalKey',
  'canonicalValue',
  'resolution',
  'rationale',
  'checkId',
  'factKey',
  'assumptionLabel',
  'sourceType',
  'sourceCitation',
  'evidenceDocumentId',
  'evidenceAttachment',
  'replacementText',
  'priorEvidence',
  'stableKey',
  'threshold',
  'fields',
  'findingId',
  'until',
]);

const OPTIONAL_STRING_OVERRIDE_KEYS = [
  'id',
  'reportTypeId',
  'runId',
  'operationId',
  'logicalKey',
  'supersedesOverrideId',
  'canonicalKey',
  'resolution',
  'rationale',
  'checkId',
  'factKey',
  'assumptionLabel',
  'sourceType',
  'sourceCitation',
  'evidenceDocumentId',
  'evidenceAttachment',
  'replacementText',
  'priorEvidence',
  'stableKey',
  'findingId',
  'until',
] as const;

function validateAndSanitizeAppendOverride(
  rawInput: unknown,
): Record<string, unknown> {
  if (!rawInput || typeof rawInput !== 'object' || Array.isArray(rawInput)) {
    throw new AuditOverrideValidationError('override payload must be an object.');
  }
  const rec = { ...(rawInput as Record<string, unknown>) };
  for (const key of Object.keys(rec)) {
    if (!ALLOWED_OVERRIDE_KEYS.has(key)) {
      throw new AuditOverrideValidationError(`Unsupported audit override field "${key}".`);
    }
  }
  if (rec.retractsOverrideId !== undefined) {
    throw new AuditOverrideValidationError('retractsOverrideId is not permitted on append overrides.');
  }
  for (const strKey of OPTIONAL_STRING_OVERRIDE_KEYS) {
    const val = rec[strKey];
    if (val !== undefined && typeof val !== 'string') {
      throw new AuditOverrideValidationError(`Invalid ${strKey}: must be a string.`);
    }
  }
  if (rec.fields !== undefined) {
    if (
      !Array.isArray(rec.fields) ||
      rec.fields.some((f) => typeof f !== 'string' || f.trim().length === 0)
    ) {
      throw new AuditOverrideValidationError('Invalid fields: must be an array of non-empty strings.');
    }
  }
  if (rec.threshold !== undefined) {
    if (typeof rec.threshold !== 'number' || !Number.isFinite(rec.threshold)) {
      throw new AuditOverrideValidationError('Invalid threshold: must be a finite number.');
    }
  }

  const rawType = rec.type;
  const rawGate = rec.gate;
  if (rawType !== undefined) {
    if (typeof rawType !== 'string' || !ALLOWED_APPEND_OVERRIDE_TYPES.has(rawType)) {
      throw new AuditOverrideValidationError(
        `Unsupported audit override type "${String(rawType)}".`,
      );
    }
    if (rawGate !== undefined && (typeof rawGate !== 'string' || !ALLOWED_APPEND_OVERRIDE_GATES.has(rawGate))) {
      throw new AuditOverrideValidationError(
        `Unsupported audit override gate "${String(rawGate)}".`,
      );
    }
    if (rawType === 'reconcile') {
      if (typeof rec.canonicalKey !== 'string' || !rec.canonicalKey.trim()) {
        throw new AuditOverrideValidationError('reconcile override requires a non-empty canonicalKey.');
      }
      if (typeof rec.canonicalValue !== 'string' && typeof rec.canonicalValue !== 'number') {
        throw new AuditOverrideValidationError('reconcile override requires canonicalValue.');
      }
      if (typeof rec.canonicalValue === 'number' && !Number.isFinite(rec.canonicalValue)) {
        throw new AuditOverrideValidationError('reconcile canonicalValue number must be finite.');
      }
    } else if (rawType === 'validate') {
      if (typeof rec.checkId !== 'string' || !rec.checkId.trim()) {
        throw new AuditOverrideValidationError('validate override requires a non-empty checkId.');
      }
    } else if (rawType === 'retain-unknown') {
      if (typeof rec.checkId !== 'string' || !rec.checkId.trim()) {
        throw new AuditOverrideValidationError('retain-unknown override requires a non-empty checkId.');
      }
      if (typeof rec.rationale !== 'string' || !rec.rationale.trim()) {
        throw new AuditOverrideValidationError('retain-unknown override requires a non-empty rationale.');
      }
    } else if (rawType === 'dismiss') {
      if (typeof rec.findingId !== 'string' || !rec.findingId.trim()) {
        throw new AuditOverrideValidationError('dismiss override requires a non-empty findingId.');
      }
    } else if (rawType === 'density') {
      if (typeof rec.threshold !== 'number' || !Number.isFinite(rec.threshold)) {
        throw new AuditOverrideValidationError('density override requires a numeric threshold.');
      }
    } else if (rawType === 'coverage') {
      if (
        !Array.isArray(rec.fields) ||
        rec.fields.length === 0 ||
        rec.fields.some((f) => typeof f !== 'string' || f.trim().length === 0)
      ) {
        throw new AuditOverrideValidationError('coverage override requires a non-empty string fields array.');
      }
    }
  } else {
    if (typeof rawGate !== 'string' || !ALLOWED_APPEND_OVERRIDE_GATES.has(rawGate)) {
      throw new AuditOverrideValidationError(
        'override must specify a supported type or gate.',
      );
    }
  }

  delete rec.createdBy;
  delete rec.createdAt;
  delete rec.sequence;
  delete rec.logicalKey;
  delete rec.supersedesOverrideId;
  delete rec.retractsOverrideId;
  return rec;
}

export type AuditOverrideMutationAction =
  | {
      kind: 'append';
      override: CreateSiteAuditOverride & { id?: string };
    }
  | {
      kind: 'retract';
      overrideId: string;
      rationale?: string;
    };

export interface ApplyAuditOverrideMutationOptions {
  operationId?: string;
  expectedAuditVersion?: number;
  expectedAuditFingerprint?: string;
  nowIso?: string;
  actor?: { uid: string; email?: string };
}

export interface ApplyAuditOverrideMutationResult {
  noop: boolean;
  idempotent: boolean;
  entry?: SiteAuditOverride;
  auditOverrides: SiteAuditOverride[];
  auditOverridesVersion: number;
  auditFingerprint: string;
}

/**
 * Single shared pure action-transform for appending, superseding, or retracting
 * a site audit override while preserving all prior records 100% verbatim.
 */
export function applyAuditOverrideMutation(
  baseSiteData:
    | {
        auditOverrides?: readonly SiteAuditOverride[];
        auditOverridesVersion?: number;
      }
    | null
    | undefined,
  action: AuditOverrideMutationAction,
  options?: ApplyAuditOverrideMutationOptions,
): ApplyAuditOverrideMutationResult {
  const existing = Array.isArray(baseSiteData?.auditOverrides)
    ? [...baseSiteData!.auditOverrides!]
    : [];
  const currentVersion = computeAuditOverridesVersion({
    auditOverrides: existing,
    auditOverridesVersion: baseSiteData?.auditOverridesVersion,
  });
  const currentFingerprint = computeAuditOverridesFingerprint(existing);
  const nowIso = options?.nowIso ?? new Date().toISOString();

  if (action.kind === 'retract') {
    if (typeof action.overrideId !== 'string' || !action.overrideId.trim()) {
      throw new AuditOverrideValidationError('overrideId is required for retract.');
    }
    if (action.rationale !== undefined && typeof action.rationale !== 'string') {
      throw new AuditOverrideValidationError('Invalid rationale: must be a string.');
    }
  }
  const sanitizedAppendRecord =
    action.kind === 'append' ? validateAndSanitizeAppendOverride(action.override) : null;

  const operationId =
    (
      options?.operationId ??
      (action.kind === 'append' && typeof sanitizedAppendRecord?.operationId === 'string'
        ? sanitizedAppendRecord.operationId
        : undefined)
    )?.trim() || undefined;

  if (operationId) {
    const alreadyCommitted = existing.find((o) => o.operationId === operationId);
    if (alreadyCommitted) {
      let expectedPayloadRecord: Record<string, unknown>;
      if (action.kind === 'append') {
        expectedPayloadRecord = sanitizedAppendRecord!;
      } else {
        const requestedOverrideId = action.overrideId.trim();
        const hasExplicitVersion = /#v\d+$/.test(requestedOverrideId);
        const baseTarget = stripOverrideVersionSuffix(requestedOverrideId);
        const committedRetractId =
          'retractsOverrideId' in alreadyCommitted && typeof alreadyCommitted.retractsOverrideId === 'string'
            ? alreadyCommitted.retractsOverrideId
            : alreadyCommitted.supersedesOverrideId;
        const committedTarget = committedRetractId
          ? existing.find((o) => o.id === committedRetractId)
          : undefined;
        // Explicit #vN targets must match the exact committed target version;
        // unversioned base-ID aliases match by base ID or logicalKey.
        const targetMatchesRequest = Boolean(
          committedTarget &&
            (hasExplicitVersion
              ? committedTarget.id === requestedOverrideId
              : committedTarget.id === requestedOverrideId ||
                stripOverrideVersionSuffix(committedTarget.id) === baseTarget ||
                deriveAuditOverrideLogicalKey(committedTarget) === requestedOverrideId ||
                deriveAuditOverrideLogicalKey(committedTarget) === baseTarget),
        );
        const resolvedTargetId = targetMatchesRequest ? committedTarget!.id : requestedOverrideId;
        expectedPayloadRecord = {
          type: 'retract',
          retractsOverrideId: resolvedTargetId,
          resolution: 'retracted',
          rationale: action.rationale?.trim() || `Retracted override ${resolvedTargetId}`,
          ...(targetMatchesRequest && committedTarget?.reportTypeId
            ? { reportTypeId: committedTarget.reportTypeId }
            : {}),
        };
      }
      if (!matchesAuditOverrideOperationPayload(alreadyCommitted, expectedPayloadRecord)) {
        throw new AuditPersistenceConflictError(
          'AUDIT_OPERATION_ID_MISMATCH',
          `Operation ID "${operationId}" was already committed with a different payload; start a new save operation.`,
          currentVersion,
          currentFingerprint,
        );
      }
      return {
        noop: false,
        idempotent: true,
        entry: alreadyCommitted,
        auditOverrides: existing,
        auditOverridesVersion: currentVersion,
        auditFingerprint: currentFingerprint,
      };
    }
  }

  if (
    options?.expectedAuditVersion !== undefined &&
    currentVersion !== options.expectedAuditVersion
  ) {
    throw new AuditPersistenceConflictError(
      'AUDIT_OVERRIDE_CONFLICT',
      `Site audit overrides changed since loaded (expected v${options.expectedAuditVersion}, found v${currentVersion}). Refresh latest state and retry.`,
      currentVersion,
      currentFingerprint,
    );
  }
  if (
    options?.expectedAuditFingerprint !== undefined &&
    currentFingerprint !== options.expectedAuditFingerprint
  ) {
    throw new AuditPersistenceConflictError(
      'AUDIT_OVERRIDE_CONFLICT',
      'Site audit overrides changed concurrently; refresh latest state and retry.',
      currentVersion,
      currentFingerprint,
    );
  }

  const active = resolveActiveAuditOverrides(existing);
  const nextVersion = currentVersion + 1;

  if (action.kind === 'retract') {
    const requestedOverrideId = action.overrideId.trim();
    const hasExplicitVersion = /#v\d+$/.test(requestedOverrideId);
    const baseTarget = stripOverrideVersionSuffix(requestedOverrideId);
    const target = active.find((o) =>
      hasExplicitVersion
        ? o.id === requestedOverrideId
        : o.id === requestedOverrideId ||
          stripOverrideVersionSuffix(o.id) === baseTarget ||
          deriveAuditOverrideLogicalKey(o) === requestedOverrideId ||
          deriveAuditOverrideLogicalKey(o) === baseTarget,
    );
    if (!target) {
      return {
        noop: true,
        idempotent: false,
        auditOverrides: existing,
        auditOverridesVersion: currentVersion,
        auditFingerprint: currentFingerprint,
      };
    }
    const retractEntry: SiteAuditOverride = {
      id: `retract:${baseTarget}#v${nextVersion}`,
      type: 'retract',
      retractsOverrideId: target.id,
      supersedesOverrideId: target.id,
      logicalKey: deriveAuditOverrideLogicalKey(target),
      sequence: nextVersion,
      ...(operationId ? { operationId } : {}),
      resolution: 'retracted',
      rationale: action.rationale?.trim() || `Retracted override ${target.id}`,
      ...(target.reportTypeId ? { reportTypeId: target.reportTypeId } : {}),
      createdAt: nowIso,
      ...(options?.actor ? { createdBy: options.actor } : {}),
    };
    const nextOverrides = [...existing, retractEntry];
    return {
      noop: false,
      idempotent: false,
      entry: retractEntry,
      auditOverrides: nextOverrides,
      auditOverridesVersion: nextVersion,
      auditFingerprint: computeAuditOverridesFingerprint(nextOverrides),
    };
  }

  const rawOverride = sanitizedAppendRecord!;
  const requestedId =
    typeof rawOverride.id === 'string' && rawOverride.id.trim().length > 0
      ? rawOverride.id.trim()
      : undefined;
  const logicalKey = deriveAuditOverrideLogicalKey({
    ...rawOverride,
    id: requestedId,
  } as SiteAuditOverride);
  const priorActive = active.find(
    (o) =>
      deriveAuditOverrideLogicalKey(o) === logicalKey ||
      (requestedId
        ? stripOverrideVersionSuffix(o.id) === stripOverrideVersionSuffix(requestedId)
        : false),
  );

  // Reject forged client-supplied lineage metadata if it contradicts authoritative state
  const rawInputObj = action.override as unknown as Record<string, unknown>;
  if (rawInputObj.supersedesOverrideId !== undefined) {
    if (
      typeof rawInputObj.supersedesOverrideId !== 'string' ||
      rawInputObj.supersedesOverrideId.trim() !== (priorActive?.id ?? '')
    ) {
      throw new AuditOverrideValidationError(
        'Forged supersedesOverrideId does not match active override history.',
      );
    }
  }
  if (rawInputObj.logicalKey !== undefined) {
    if (
      typeof rawInputObj.logicalKey !== 'string' ||
      rawInputObj.logicalKey.trim() !== logicalKey
    ) {
      throw new AuditOverrideValidationError(
        'Forged logicalKey does not match canonical override identity.',
      );
    }
  }

  let entryId: string;
  if (requestedId) {
    const baseReq = stripOverrideVersionSuffix(requestedId);
    const collision = existing.some(
      (o) => o.id === requestedId || stripOverrideVersionSuffix(o.id) === baseReq,
    );
    entryId = collision ? `${baseReq}#v${nextVersion}` : requestedId;
  } else {
    entryId = `audit-override-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  const entry = {
    ...(rawOverride as unknown as CreateSiteAuditOverride),
    id: entryId,
    logicalKey,
    sequence: nextVersion,
    ...(operationId ? { operationId } : {}),
    ...(priorActive ? { supersedesOverrideId: priorActive.id } : {}),
    createdAt: nowIso,
    ...(options?.actor ? { createdBy: options.actor } : {}),
  } as SiteAuditOverride;

  const nextOverrides = [...existing, entry];
  return {
    noop: false,
    idempotent: false,
    entry,
    auditOverrides: nextOverrides,
    auditOverridesVersion: nextVersion,
    auditFingerprint: computeAuditOverridesFingerprint(nextOverrides),
  };
}
